// =============================================================================
// ROLE EVENTS — sub-engagements within a single employment role.
//
// roles (1) -- (many) role_events. One employment can deliver many tournaments /
// programmes, each with its own window and outcomes. Real case: ONE "FIFA World
// Cup 2026" employment that delivered BOTH the Club World Cup 2025 AND the World
// Cup 2026. The flat `roles` row cannot express that; this child table can.
//
// Mirrors the existing roles -> achievements parent/child shape (soft FK + index,
// no DB-level REFERENCES, matching the repo style). ADDITIVE: a corpus with zero
// role_events behaves exactly as today. An optional publicEventKey links to the
// shared public_events lake (store-a-ref, the distilled_profiles discipline), so a
// sub-event's dates can be public truth rather than a re-typed guess.
// =============================================================================

import { date, index, integer, jsonb, pgTable, text, uuid, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

export const roleEvents = pgTable(
  'role_events',
  {
    id: id(),
    // Denormalised for the user-scoped query pattern used across the corpus tables.
    userId: text('user_id').notNull(),
    // The parent employment. Soft FK -> roles.id (indexed, enforced in the service).
    roleId: uuid('role_id').notNull(),
    // Optional link to the shared public-event reference (-> public_events.canonical_key).
    // NULL for a private sub-engagement with no public event. When set, verified
    // dates/hosts come from the lake, not re-typed here.
    publicEventKey: varchar('public_event_key', { length: 160 }),
    // User-facing label, e.g. 'Club World Cup 2025 delivery'.
    label: varchar('label', { length: 240 }).notNull(),
    // The candidate's role on THIS sub-event if it differs from the parent title.
    // NULL = inherits the parent role's title.
    title: varchar('title', { length: 200 }),
    // Sub-event window (may be narrower than the parent role). NULL start falls
    // back to the linked public_events.startDate at read time.
    startDate: date('start_date'),
    endDate: date('end_date'),
    isCurrent: integer('is_current').default(0).notNull(), // same 0|1 convention as roles
    summary: text('summary'),
    sortOrder: integer('sort_order').default(0).notNull(),
    metadata: jsonb('metadata'),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('role_events_user_idx').on(t.userId),
    roleIdx: index('role_events_role_idx').on(t.roleId),
    eventKeyIdx: index('role_events_event_key_idx').on(t.publicEventKey),
  }),
);
